import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import * as M from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconCalendar } from '@tabler/icons-react';
import dayjs from 'dayjs';

import MantineProvider from 'providers/MantineProvider';
import { hasAccessTo } from 'store/selectors/auth';
import HolidayBrowser from 'features/holidayClosures/HolidayBrowser';
import {
  holidayCountries,
  selectHoliday,
} from 'features/holidayClosures/holidays';
import {
  holidayApi,
  errorMessage,
  HolidayInputs,
  RunRow,
} from 'features/holidayClosures/api';
import {
  canApply,
  codes,
  columns,
  dateValue,
  defaults,
  reportCsv,
  Review,
  validInputs,
} from 'features/holidayClosures/model';

function HolidayClosuresContent() {
  const scopes = useSelector(
    (state: { auth: { scopes?: Set<string> } }) => state.auth.scopes,
  );
  const allowed = hasAccessTo(scopes, [
    'school_management',
    'schedule_management',
  ]);
  const [inputs, setInputs] = useState<HolidayInputs>(defaults);
  const [runId, setRunId] = useState('');
  const [review, setReview] = useState<Review | null>(null);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const mounted = useRef(true);
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [holidayBrowserOpen, setHolidayBrowserOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [pastRunId, setPastRunId] = useState('');
  const [action, setAction] = useState('');
  const [reflowOnly, setReflowOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ key: keyof RunRow; descending: boolean }>({
    key: 'school_id',
    descending: false,
  });
  const config = useQuery(['holidayClosureConfig'], holidayApi.config, {
    enabled: allowed,
    retry: false,
  });
  const environment = config.data?.environment ?? '';
  const ready = ['staging', 'production', 'local'].includes(environment);
  const reportQuery = useQuery(
    ['holidayClosureRun', environment, runId, action, reflowOnly],
    () => holidayApi.report(runId, action, reflowOnly),
    {
      enabled: allowed && ready && Boolean(runId),
      retry: false,
      refetchInterval: (data) =>
        data?.status === 'queued' || data?.status === 'running' ? 3000 : false,
    },
  );
  const report = reportQuery.data;
  const pending = Boolean(
    runId &&
      (!report || report.status === 'queued' || report.status === 'running') &&
      !reportQuery.isError,
  );
  const locked = busy || pending;
  const eligible =
    !locked &&
    !reportQuery.isError &&
    canApply(review, report, inputs, environment);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  useEffect(() => {
    setPage(1);
  }, [runId, search, action, reflowOnly]);

  const rows = useMemo(
    () =>
      [...(report?.rows ?? [])]
        .filter((row) =>
          columns.some(([key]) =>
            String(row[key] ?? '')
              .toLowerCase()
              .includes(search.toLowerCase()),
          ),
        )
        .sort((a, b) => {
          const left = a[sort.key] ?? '';
          const right = b[sort.key] ?? '';
          const comparison =
            typeof left === 'number' && typeof right === 'number'
              ? left - right
              : String(left).localeCompare(String(right), undefined, {
                  numeric: true,
                });
          return sort.descending ? -comparison : comparison;
        }),
    [report, search, sort],
  );
  const pageCount = Math.max(1, Math.ceil(rows.length / 50));
  const currentPage = Math.min(page, pageCount);

  function change<K extends keyof HolidayInputs>(
    key: K,
    value: HolidayInputs[K],
  ) {
    setInputs((old) => ({ ...old, [key]: value }));
    setReview(null);
    setConfirmOpen(false);
    setConfirmation('');
  }

  async function start(apply: boolean) {
    if (lock.current || locked || !ready || !validInputs(inputs)) return;
    if (apply && (!eligible || confirmation !== 'APPLY' || !review)) return;
    lock.current = true;
    setBusy(true);
    setError('');
    const snapshot = { ...inputs };
    const reviewedId = review?.id ?? '';
    setReview(null);
    setConfirmOpen(false);
    setConfirmation('');
    try {
      const result = apply
        ? await holidayApi.apply(
            snapshot,
            reviewedId,
            environment,
            confirmation,
          )
        : await holidayApi.dryRun(snapshot);
      if (!mounted.current) return;
      if (result.environment !== environment || result.dry_run === apply)
        throw Error(
          'Unexpected environment or run mode. Reload and review a new dry run.',
        );
      setRunId(result.run_id);
      setAction('');
      setReflowOnly(false);
      setSearch('');
      if (!apply)
        setReview({
          id: result.run_id,
          inputs: snapshot,
          environment,
          expires:
            Date.now() + (config.data?.review_ttl_seconds ?? 3600) * 1000,
        });
    } catch (err) {
      if (mounted.current) setError(errorMessage(err));
    } finally {
      lock.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  function loadPast() {
    if (locked || lock.current) return;
    setReview(null);
    setConfirmOpen(false);
    setConfirmation('');
    setError('');
    setAction('');
    setReflowOnly(false);
    setSearch('');
    setRunId(pastRunId.trim());
  }

  function download() {
    const url = URL.createObjectURL(
      new Blob([reportCsv(rows)], { type: 'text/csv;charset=utf-8;' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `holiday-closures-${environment}-${runId}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  if (!allowed)
    return (
      <M.Alert color="red">
        School or schedule management access is required.
      </M.Alert>
    );

  return (
    <M.Container fluid px="xl" py="xl">
      <M.Group position="apart" mb="xl">
        <div>
          <M.Title order={1}>Holiday closures</M.Title>
          <M.Text color="dimmed">
            Preview school closures and calendar re-flows before applying them.
          </M.Text>
        </div>
        <M.Badge color={environment === 'production' ? 'red' : 'blue'}>
          Environment: {environment || 'Connecting…'}
        </M.Badge>
      </M.Group>
      {config.isError && (
        <M.Alert color="red" title="Connection unavailable" mb="md">
          {errorMessage(config.error)}{' '}
          <M.Button
            size="xs"
            variant="subtle"
            onClick={() => void config.refetch()}
          >
            Retry connection
          </M.Button>
        </M.Alert>
      )}
      {error && (
        <M.Alert color="red" title="Request failed" mb="md">
          {error}
        </M.Alert>
      )}
      <M.Grid gutter="xl">
        <M.Grid.Col md={4} lg={3}>
          <M.Paper withBorder p="lg" radius="md">
            <M.Stack>
              <M.Button
                variant="light"
                leftIcon={<IconCalendar size={18} />}
                disabled={locked}
                onClick={() => setHolidayBrowserOpen(true)}
              >
                Choose from public holidays
              </M.Button>
              <DateInput
                label="Holiday date"
                description="Choose a public holiday above or enter a date manually"
                value={dateValue(inputs.holiday_date)}
                valueFormat="YYYY-MM-DD"
                placeholder="YYYY-MM-DD"
                icon={<IconCalendar size={18} />}
                clearable
                fixOnBlur
                disabled={locked}
                onChange={(value) =>
                  change(
                    'holiday_date',
                    value ? dayjs(value).format('YYYY-MM-DD') : '',
                  )
                }
              />
              <M.Select
                label="Holiday code"
                data={codes}
                value={inputs.code}
                disabled={locked}
                onChange={(value) => change('code', value ?? '')}
              />
              <M.Select
                label="Country"
                data={holidayCountries}
                value={inputs.country}
                disabled={locked}
                onChange={(value) => {
                  if (!value) return;
                  setInputs((old) => ({
                    ...old,
                    country: value,
                    states: '',
                    school_ids: '',
                  }));
                  setReview(null);
                  setConfirmOpen(false);
                  setConfirmation('');
                }}
              />
              <M.TextInput
                label="States / provinces"
                description="Empty = all states / provinces the holiday applies to"
                placeholder={inputs.country === 'US' ? 'NY,CA' : 'ON,BC'}
                value={inputs.states}
                disabled={locked}
                onChange={(event) =>
                  change('states', event.currentTarget.value)
                }
              />
              <M.Textarea
                label="School IDs"
                description="Comma-separated; empty = all eligible schools"
                autosize
                minRows={2}
                value={inputs.school_ids}
                disabled={locked}
                onChange={(event) =>
                  change('school_ids', event.currentTarget.value)
                }
              />
              <M.TextInput
                label="No re-flow websites"
                value={inputs.no_reflow_websites}
                disabled={locked}
                onChange={(event) =>
                  change('no_reflow_websites', event.currentTarget.value)
                }
              />
              <M.TextInput
                label="No re-flow school IDs"
                value={inputs.no_reflow_school_ids}
                disabled={locked}
                onChange={(event) =>
                  change('no_reflow_school_ids', event.currentTarget.value)
                }
              />
              <M.Checkbox
                label="Skip all re-flows"
                checked={inputs.no_reflow}
                disabled={locked}
                onChange={(event) =>
                  change('no_reflow', event.currentTarget.checked)
                }
              />
              <M.Text size="sm" color="dimmed">
                Dry runs do not change school calendars. Apply requires a
                completed, matching dry run and typed confirmation. Reviews
                expire after one hour.
              </M.Text>
              <M.Button
                disabled={!ready || locked || !validInputs(inputs)}
                loading={busy}
                onClick={() => void start(false)}
              >
                Dry run
              </M.Button>
              <M.Button
                color={environment === 'production' ? 'red' : 'orange'}
                disabled={!eligible}
                onClick={() => {
                  setConfirmation('');
                  setConfirmOpen(true);
                }}
              >
                Apply to {environment || 'environment'}…
              </M.Button>
            </M.Stack>
          </M.Paper>
          <M.Paper withBorder p="lg" mt="md" radius="md">
            <M.Stack spacing="sm">
              <M.TextInput
                label="Past run ID"
                value={pastRunId}
                disabled={locked}
                onChange={(event) => setPastRunId(event.currentTarget.value)}
              />
              <M.Button
                variant="light"
                disabled={
                  !ready ||
                  locked ||
                  !/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(
                    pastRunId.trim(),
                  )
                }
                onClick={loadPast}
              >
                Load past run
              </M.Button>
              <M.Text size="xs" color="dimmed">
                View-only; looking up a run never enables Apply. Reports are
                retained for 14 days.
              </M.Text>
            </M.Stack>
          </M.Paper>
        </M.Grid.Col>
        <M.Grid.Col md={8} lg={9}>
          <M.Stack>
            <M.Group position="apart">
              <div>
                <M.Title order={2}>
                  {report?.params?.dry_run === false
                    ? 'Apply run'
                    : 'Run preview'}
                </M.Title>
                <M.Text size="sm" color="dimmed">
                  {runId || 'Run a dry run to see the plan.'}
                </M.Text>
              </div>
              <M.Group>
                <M.Badge color={report?.status === 'failed' ? 'red' : 'blue'}>
                  {report?.status ?? (runId ? 'Loading' : 'No run')}
                </M.Badge>
                {report?.params && (
                  <M.Badge>
                    {report.params.dry_run ? 'DRY RUN' : 'APPLY RUN'}
                  </M.Badge>
                )}
                <M.Button
                  variant="light"
                  disabled={!runId || busy}
                  loading={reportQuery.isFetching}
                  onClick={() => void reportQuery.refetch()}
                >
                  Refresh run
                </M.Button>
              </M.Group>
            </M.Group>
            {reportQuery.isError && (
              <M.Alert color="red">{errorMessage(reportQuery.error)}</M.Alert>
            )}
            {report?.status === 'unknown' && (
              <M.Alert color="yellow">
                This run was not found in {environment}. Check the ID and
                environment; reports expire after 14 days.
              </M.Alert>
            )}
            {report?.error && (
              <M.Alert title="Run failed" color="red">
                {report.error}
              </M.Alert>
            )}
            {report?.params && (
              <M.Text size="sm">
                {report.params.date} · {report.params.code} ·{' '}
                {report.params.country} · {report.environment}
              </M.Text>
            )}
            <M.SimpleGrid cols={4} breakpoints={[{ maxWidth: 'sm', cols: 2 }]}>
              {(
                [
                  ['schools', 'Schools'],
                  ['close', 'Close'],
                  ['reflows_queued', 'Re-flows queued'],
                  ['reflow_rows_in_preview', 'Re-flow rows in preview'],
                ] as const
              ).map(([key, label]) => (
                <M.Paper withBorder radius="md" p="md" key={key}>
                  <M.Text size="sm" color="dimmed">
                    {label}
                  </M.Text>
                  <M.Title order={2}>{report?.summary?.[key] ?? '—'}</M.Title>
                </M.Paper>
              ))}
            </M.SimpleGrid>
            <M.SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
              {(
                [
                  ['actions', 'Actions'],
                  ['reflow_reasons', 'Re-flow reasons'],
                ] as const
              ).map(([key, label]) => (
                <M.Paper withBorder radius="md" p="md" key={key}>
                  <M.Text weight={600}>{label}</M.Text>
                  {Object.entries(report?.summary?.[key] ?? {}).map(
                    ([reason, count]) => (
                      <M.Group position="apart" key={reason}>
                        <M.Text size="sm">{reason}</M.Text>
                        <M.Text size="sm">{count}</M.Text>
                      </M.Group>
                    ),
                  )}
                </M.Paper>
              ))}
            </M.SimpleGrid>
            <M.Group align="flex-end">
              <M.TextInput
                label="Search results"
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                placeholder="School, state, action or reason"
                style={{ flex: 1 }}
              />
              <M.Select
                label="Action"
                value={action}
                onChange={(value) => setAction(value ?? '')}
                data={[
                  { value: '', label: 'All actions' },
                  { value: 'close', label: 'Close' },
                  { value: 'skip', label: 'Skip' },
                ]}
              />
              <M.Checkbox
                label="Re-flows only"
                checked={reflowOnly}
                mb={8}
                onChange={(event) => setReflowOnly(event.currentTarget.checked)}
              />
              <M.Button
                variant="light"
                disabled={!rows.length}
                onClick={download}
              >
                Export CSV
              </M.Button>
            </M.Group>
            <M.ScrollArea>
              <M.Table striped highlightOnHover withBorder>
                <thead>
                  <tr>
                    {columns.map(([key, label]) => (
                      <th
                        key={key}
                        aria-sort={
                          sort.key === key
                            ? sort.descending
                              ? 'descending'
                              : 'ascending'
                            : 'none'
                        }
                      >
                        <M.UnstyledButton
                          onClick={() =>
                            setSort({
                              key,
                              descending: sort.key === key && !sort.descending,
                            })
                          }
                        >
                          {label}
                          {sort.key === key
                            ? sort.descending
                              ? ' ↓'
                              : ' ↑'
                            : ''}
                        </M.UnstyledButton>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows
                    .slice((currentPage - 1) * 50, currentPage * 50)
                    .map((row, index) => (
                      <tr key={`${row.school_id}-${index}`}>
                        {columns.map(([key]) => (
                          <td key={key}>
                            {typeof row[key] === 'boolean'
                              ? row[key]
                                ? 'Yes'
                                : 'No'
                              : row[key] ?? '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  {!rows.length && (
                    <tr>
                      <td colSpan={columns.length}>No results to display.</td>
                    </tr>
                  )}
                </tbody>
              </M.Table>
            </M.ScrollArea>
            <M.Group position="apart">
              <M.Text size="sm" color="dimmed">
                {rows.length} displayed / {report?.matching_rows ?? 0} matching
                rows. Server limit: 3,000. CSV includes the filtered returned
                rows.
              </M.Text>
              <M.Pagination
                total={pageCount}
                value={currentPage}
                onChange={setPage}
              />
            </M.Group>
          </M.Stack>
        </M.Grid.Col>
      </M.Grid>
      {holidayBrowserOpen && (
        <HolidayBrowser
          country={inputs.country}
          date={inputs.holiday_date}
          onClose={() => setHolidayBrowserOpen(false)}
          onSelect={(holiday) => {
            if (locked) return;
            setInputs((old) => selectHoliday(old, holiday));
            setReview(null);
            setConfirmOpen(false);
            setConfirmation('');
            setHolidayBrowserOpen(false);
          }}
        />
      )}
      <M.Modal
        opened={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setConfirmation('');
        }}
        title={`Apply holiday closures to ${environment}`}
        centered
      >
        <M.Stack>
          <M.Alert color={environment === 'production' ? 'red' : 'orange'}>
            This writes to {environment}: closes {report?.summary?.close ?? 0}{' '}
            schools on {inputs.holiday_date} and queues{' '}
            {report?.summary?.reflows_queued ?? 0} re-flows.
          </M.Alert>
          <M.Text size="sm">
            Code: {inputs.code}
            <br />
            Reviewed dry run: {review?.id}
          </M.Text>
          <M.TextInput
            label="Type APPLY to confirm"
            value={confirmation}
            onChange={(event) => setConfirmation(event.currentTarget.value)}
            autoComplete="off"
          />
          <M.Group position="right">
            <M.Button
              variant="default"
              onClick={() => {
                setConfirmOpen(false);
                setConfirmation('');
              }}
            >
              Cancel
            </M.Button>
            <M.Button
              color="red"
              disabled={!eligible || confirmation !== 'APPLY'}
              onClick={() => void start(true)}
            >
              Confirm apply to {environment}
            </M.Button>
          </M.Group>
        </M.Stack>
      </M.Modal>
    </M.Container>
  );
}

export default function HolidayClosures() {
  return (
    <MantineProvider>
      <HolidayClosuresContent />
    </MantineProvider>
  );
}
